import React, { useState, useEffect, useRef, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import api from '@lib/api';
import { isEqual } from 'lodash';
import { Layout, Spin, Modal, Typography, Button, message } from 'antd';
import screenfull from 'screenfull';
import isMobile from 'is-mobile';
import { LoadingOutlined } from '@ant-design/icons';
import useStores from '@lib/hooks/useStores';
import useVideoContext from '@lib/hooks/useVideoContext';
import Player from '@lib/player';
import VideoTimeline from '@components/VideoTimeline';
import ChapterBox from '@components/ChapterBox';
import { IUserStore } from '@stores/userStore';
import { IVideoStore } from '@stores/videoStore';
import { useUIStore } from '@stores/ui';
import { IChapter } from '@interfaces/pose.interface';
import { HoveredChapter } from '@interfaces/video.interface';

import { ReactComponent as IconFullscreen } from '@svg/icon-editor-fullscreen.svg';
import { ReactComponent as IconRefreshInverted } from '@svg/icon-refresh-inverted.svg';
import { scMixinAbsoluteFull } from '@utils/emotion/mixins';
import { scFunctionZIndex } from '@utils/emotion/functions';

const { Content: AntdContent } = Layout;
const { Title, Text } = Typography;

type MatchParams = {
  videoId: string;
};

let isModifiedCalculateTimerToken: any;
const transcodingFrameRate = parseFloat(process.env.REACT_APP_TRANSCODING_FRAME_RATE!);

const ChapterEditor: React.FC = observer(() => {
  const { t } = useTranslation();
  const { videoId } = useParams<keyof MatchParams>();
  const { userStore, videoStore } = useStores<{
    userStore: IUserStore;
    videoStore: IVideoStore;
  }>();
  const { isSiderCollapsed, toggleSider, setOverrideTheme } = useUIStore();
  const navigate = useNavigate();
  const location = useLocation();
  const fetchVideoDataTimeoutToken = useRef<number>();
  const [isFullscreen, setFullscreen] = useState(false);
  const [{ data, loading, error }, fetch] = useVideoContext(videoId);
  const [chapterData, updateChapterData] = useState<IChapter[]>([]);
  const [isOverrideModify, setOverrideModify] = useState(false);
  const [isModified, setChapterModified] = useState(false);
  const [onSubmitting, setSubmitting] = useState(false);
  const [isOnceSubmitted, setOnceSubmitted] = useState(false);
  const [hoveredChapter, setHoveredChapter] = useState<HoveredChapter>({
    idx: -1,
    from: null,
  });
  const [clickedChapter, setClickedChapter] = useState<HoveredChapter>({
    idx: -1,
    from: null,
  });
  const initialSiderCollapsed = useRef(isSiderCollapsed);

  useEffect(() => {
    function blurHandler(e: any): void {
      const path = e.path as any;
      if (
        path.some(
          (element: HTMLElement) =>
            'classList' in element &&
            (element.classList.contains('chapter-range') ||
              element.classList.contains('screen') ||
              element.classList.contains('timeline-playhead-background')),
        )
      )
        return;

      if (clickedChapter !== null) {
        setClickedChapter({
          idx: -1,
          from: null,
        });
      }
    }
    document.body.addEventListener('click', blurHandler);
    return () => {
      document.body.removeEventListener('click', blurHandler);
    };
  }, [clickedChapter]);

  const fullscreenCallback = () => {
    if (!screenfull.isEnabled) return;

    if (screenfull.isFullscreen) {
      setFullscreen(true);
      toggleSider(true);
    } else {
      setFullscreen(false);
      toggleSider(initialSiderCollapsed.current);
    }
  };

  useEffect(() => {
    if (screenfull.isEnabled) {
      screenfull.on('change', fullscreenCallback);
    }

    return () => {
      clearTimeout(fetchVideoDataTimeoutToken.current);
      if (screenfull.isEnabled) {
        screenfull.off('change', fullscreenCallback);
        screenfull.exit();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFullscreen = () => {
    if (!screenfull.isEnabled) return;

    screenfull.toggle();
  };

  const shortcutHandler = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;

      if (videoStore.playerRef && (target.nodeName === 'BODY' || target.nodeName === 'VIDEO')) {
        const { playerRef } = videoStore;
        const currentTime = playerRef.currentTime();

        switch (e.code) {
          case 'KeyF': // Fullscreen
            toggleFullscreen();
            return;
          case 'Space': // Play/Pause
            e.preventDefault();
            if (playerRef.paused()) {
              playerRef.play();
            } else {
              playerRef.pause();
            }
            break;
          case 'ArrowLeft': // -1 frame
            e.preventDefault();
            playerRef.currentTime(currentTime - (e.shiftKey ? 5 : 1) / transcodingFrameRate);
            break;
          case 'ArrowRight': // +1 frame
            e.preventDefault();
            playerRef.currentTime(currentTime + (e.shiftKey ? 5 : 1) / transcodingFrameRate);
            break;
          default:
        }
      }
    },
    [videoStore],
  );

  useEffect(() => {
    setOverrideTheme('dark');
    window.addEventListener('keydown', shortcutHandler);

    return () => {
      setOverrideTheme(null);
      videoStore.resetStore();
      window.removeEventListener('keydown', shortcutHandler);
    };
  }, [shortcutHandler, setOverrideTheme, videoStore]);

  // 가져온 비디오의 context 확인 => 챕터 업데이트
  useEffect(() => {
    // console.log(`API/Chapter [${loading ? 'Loading' : 'Loaded'}]${error ? ' [Error]' : ''}`, data);
    if (loading) return;

    if (error) {
      Modal.error({
        title: t('error.api.common.retrieve.server.title'),
        content: t('error.api.chaptereditor.failed'),
        okText: t('error.back'),
        onOk() {
          navigate(-1);
        },
      });
    }

    if (data && !error) {
      const { title, pose, type, jobs } = data;

      if (jobs.transcoding === 'processing') {
        Modal.warn({
          title: t('error.api.common.retrieve.server.title'),
          content: t('error.api.edit.ontranscoding'),
          okText: t('error.back'),
          onOk: () => {
            navigate(-1);
          },
        });

        return;
      }

      if (type === 'explanation') {
        Modal.warn({
          title: t('video.common.warn.title'),
          content: t('video.common.warn.editor.explanation.chapter'),
          onOk: () => {
            navigate(-1);
          },
        });

        return;
      }

      videoStore.setVideoData({ videoId, title });
      if (Object.keys(pose).length > 0 && pose.chapters.length > 0) {
        updateChapterData(JSON.parse(JSON.stringify(pose.chapters)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error]);

  // 챕터 수정된 사항이 있는지 체크
  useEffect(() => {
    clearTimeout(isModifiedCalculateTimerToken);
    if (data && !loading && chapterData.length) {
      isModifiedCalculateTimerToken = window.setTimeout(() => {
        if ('chapters' in data.pose) {
          const original = [...data.pose.chapters].sort((a, b) => a.chapterIdx - b.chapterIdx);
          const clone = [...chapterData].sort((a, b) => a.chapterIdx - b.chapterIdx);

          setChapterModified(!isEqual(original, clone));
        }
      }, 50);
    }
  }, [loading, data, chapterData]);

  // 챕터 저장 이후 fetch - GPU 처리 완료 이후 DB와의 싱크 불일치으로 인해 2초 딜레이
  useEffect(() => {
    if (isOnceSubmitted && userStore.completedProcessingVideoIds.length) {
      userStore.completedProcessingVideoIds.forEach((id) => {
        if (location.pathname === `/video/${id}/editor/chapter`) {
          fetchVideoDataTimeoutToken.current = window.setTimeout(() => {
            fetch();
          }, 2000);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.completedProcessingVideoIds.length, isOnceSubmitted, location]);

  // BeforeUnload Event Handler
  useEffect(() => {
    const message = t('video.editor.alert.back');

    function handler(e: BeforeUnloadEvent) {
      if (isModified) e.returnValue = message;

      return '';
    }

    if (isModified) {
      window.onbeforeunload = handler;
      window.onunload = handler;
    } else {
      window.onbeforeunload = null;
      window.onunload = null;
    }
    // const unblock = history.block(() => {
    //   if (isModified) return message;

    //   return undefined;
    // });

    return () => {
      // unblock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModified, history]);

  const submit = async () => {
    if (!data) return;

    const hasChapters = 'chapters' in data.pose;
    const updatedData = chapterData.map(({ chapterIdx: idx, label, startTime, endTime }) => ({
      idx,
      label,
      startTime,
      endTime,
    }));
    const stateInitialize = () => {
      fetchVideoDataTimeoutToken.current = window.setTimeout(() => {
        setSubmitting(false);
        setOverrideModify(false);
        setOnceSubmitted(true);
        fetch();
      }, 2000);
    };

    setSubmitting(true);
    try {
      if (hasChapters) {
        const { status } = await api.patch(`/video/${videoId}/chapter`, updatedData);
        switch (status) {
          case 201:
            message.success(t('video.editor.chapter.title.complete'), 4);
            break;
          default:
            message.loading(t('video.editor.chapter.submitting.message'), 4);

            if (userStore.currentlyProcessingVideoIds.includes(videoId!)) {
              userStore.ejectProcessingVideo(videoId!);
            }
            userStore.injectProcessingVideo(videoId!);
        }
      } else {
        await api.post(`/video/${videoId}/pose`, updatedData);
        message.loading(t('video.editor.chapter.submitting.message'), 4);

        if (userStore.currentlyProcessingVideoIds.includes(videoId!)) {
          userStore.ejectProcessingVideo(videoId!);
        }
        userStore.injectProcessingVideo(videoId!);
      }
      stateInitialize();
    } catch {
      setSubmitting(false);
      Modal.error({
        title: t('error.api.common.submit.server.title'),
        content: t('error.api.chaptereditor.failed.submit'),
        okText: t('a11y.ok'),
      });
    }
  };

  const confirmOverrideSubmit = () => {
    Modal.confirm({
      title: t('video.editor.chapter.override.title'),
      content: t('video.editor.chapter.override.content'),
      okText: t('a11y.ok'),
      cancelText: t('a11y.cancel'),
      onOk: () => {
        submit();
      },
      onCancel: () => null,
    });
  };

  if (loading || error || !data) {
    return (
      <Content>
        <div className="loading-screen">
          <div className="dimmer" />
          <div className="inner">
            <Spin
              indicator={
                <LoadingOutlined
                  style={{
                    fontSize: 48,
                    color: 'rgb(255, 255, 255, 0.75)',
                  }}
                />
              }
            />
          </div>
        </div>
      </Content>
    );
  }

  if (!videoId) return <></>;

  return (
    <Content>
      <HeaderBlock
        fullscreen={isFullscreen}
        editing={loading ? false : data.jobs.poseEstimation === 'pending' || isOverrideModify}
        key="header"
      >
        <Typography>
          {(data.jobs.poseEstimation === 'pending' || isOverrideModify) && (
            <>
              <Title>{t('video.editor.chapter.title.pending')}</Title>
              <Text>{t('video.editor.chapter.desc.pending')}</Text>
            </>
          )}
          {data.jobs.poseEstimation === 'processing' && !isOverrideModify && (
            <>
              <Title>{t('video.editor.chapter.title.processing')}</Title>
              <Text>{t('video.editor.chapter.desc.processing')}</Text>
            </>
          )}
          {data.jobs.poseEstimation === 'complete' && !isOverrideModify && (
            <>
              <Title>{t('video.editor.chapter.title.complete')}</Title>
              <Text>{t('video.editor.chapter.desc.complete')}</Text>
            </>
          )}
        </Typography>
        <div className="action-area">
          {!isOverrideModify && data.jobs.poseEstimation === 'complete' && (
            <Button
              type="primary"
              size="large"
              icon={
                <IconRefreshInverted
                  style={{ verticalAlign: 'top', marginTop: 5, marginRight: 8 }}
                />
              }
              style={{
                width: 'auto',
                minWidth: 160,
                backgroundColor: '#8100FF',
                borderColor: '#8100FF',
                color: '#F5F6F8',
              }}
              onClick={() => setOverrideModify(true)}
            >
              {t('video.editor.chapter.form.override')}
            </Button>
          )}
          {!isOverrideModify && data?.jobs.poseEstimation === 'complete' && (
            <Button
              type="primary"
              size="large"
              style={{
                width: 'auto',
                minWidth: 160,
                backgroundColor: '#52C41A',
                borderColor: '#52C41A',
                color: '#090909',
              }}
              onClick={() => {
                navigate(`/video/${videoId}/editor/coords`);
              }}
            >
              {t('video.editor.chapter.form.next')}&nbsp;&gt;
            </Button>
          )}
        </div>
        <div className="util-area">
          {/* <Tooltip placement="left" title={`${t('a11y.fullscreen')} (f)`}> */}
          <button
            className="btn-fullscreen"
            aria-label="Fullscreen"
            onClick={() => toggleFullscreen()}
          >
            <IconFullscreen />
          </button>
          {/* </Tooltip> */}
        </div>
      </HeaderBlock>

      <EditorBlock fullscreen={isFullscreen} key="editor">
        <div className="video-area">
          <div className="screen-box">
            <Player
              autoplay={false}
              muted={isMobile({ tablet: true })}
              controls
              inactivityTimeout={0}
              controlBar={{
                children: [
                  'playToggle',
                  // 'currentTimeDisplay',
                  // 'timeDivider',
                  // 'durationDisplay',
                  // 'progressControl',
                  // 'remainingTimeDisplay',
                  'customControlSpacer',
                  'playbackRateMenuButton',
                  'volumePanel',
                  'fullscreenToggle',
                ],
              }}
            >
              <source
                src={data.transcodings[data.apiVersion === 'v1' ? 1 : 0].url}
                type={data.apiVersion === 'v1' ? 'video/mp4' : 'application/x-mpegURL'}
              />
            </Player>
          </div>
          <div className="chapter-box">
            <ChapterBox
              editable={data.jobs.poseEstimation === 'pending' || isOverrideModify}
              isFullscreen={isFullscreen}
              duration={data.duration ?? videoStore.playerData.duration}
              currentTime={videoStore.playerData.currentTime}
              chapterData={chapterData}
              updateChapterData={updateChapterData}
              hoveredChapter={hoveredChapter}
              onChapterMouseOver={(data: HoveredChapter) => setHoveredChapter(data)}
              clickedChapter={clickedChapter}
              onChapterClickChapterEditor={(data: HoveredChapter) => setClickedChapter(data)}
            />
          </div>
        </div>
        <div className="timeline-area">
          <VideoTimeline
            type="chapter"
            editable={data.jobs.poseEstimation === 'pending' || isOverrideModify}
            duration={data.duration ?? videoStore.playerData.duration}
            currentTime={videoStore.playerData.currentTime}
            currentProgress={videoStore.currentProgress}
            chapterData={chapterData}
            updateChapterData={updateChapterData}
            hoveredChapter={hoveredChapter}
            onChapterMouseOver={(data: HoveredChapter) => setHoveredChapter(data)}
            clickedChapter={clickedChapter}
            onChapterClick={(data: HoveredChapter) => setClickedChapter(data)}
          />
        </div>
      </EditorBlock>

      <FooterBlock fullscreen={isFullscreen} key="footer">
        <div className="action-area">
          {data.jobs.poseEstimation === 'pending' || isModified ? (
            <Button
              type="primary"
              size="large"
              loading={onSubmitting}
              disabled={!chapterData.length}
              onClick={() => confirmOverrideSubmit()}
            >
              {t('video.editor.chapter.form.submit')}
            </Button>
          ) : isOverrideModify ? (
            <Button
              type="primary"
              size="large"
              loading={onSubmitting}
              disabled={!isModified || !chapterData.length}
              onClick={() => submit()}
            >
              {t('video.editor.chapter.form.submit')}
            </Button>
          ) : null}
        </div>
      </FooterBlock>
    </Content>
  );
});

const Content = styled(AntdContent)`
  display: flex;
  flex-direction: column;

  .loading-screen {
    display: flex;
    justify-content: center;
    align-items: center;
    ${scMixinAbsoluteFull};
    ${scFunctionZIndex('loading')};

    .dimmer {
      ${scMixinAbsoluteFull};
      z-index: 0;
      background-color: rgba(0, 0, 0, 0.6);
    }

    .inner {
      position: relative;
      z-index: 1;
    }
  }
`;

interface StyledHeaderBlockProps {
  fullscreen: boolean;
  editing: boolean;
}

const HeaderBlock = styled.div<StyledHeaderBlockProps>`
  display: block;
  position: relative;
  padding: ${({ editing, fullscreen }) =>
    editing && fullscreen ? '18px 36px 0 22px' : '18px 36px 30px'};

  .action-area {
    display: inline-flex;
    float: right;
    max-height: 32px;

    .ant-btn {
      width: 160px;

      & + .ant-btn {
        margin-left: 20px;
      }
    }
  }

  & > article.ant-typography {
    overflow: hidden;
    height: ${({ fullscreen }) => (fullscreen ? '0' : 'auto')};
    margin-bottom: ${({ fullscreen }) => (fullscreen ? '0' : '16px')};
  }

  h1.ant-typography {
    font-weight: bold;
    font-size: 23px;
    line-height: 34px;
    letter-spacing: 0.01em;
  }

  span.ant-typography {
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0.01em;
    white-space: pre;
    color: #b6b7b8;
  }

  .util-area {
    position: absolute;
    right: 48px;
    bottom: -26px;
  }

  .btn-fullscreen {
    display: block;
    margin: -6px;
    padding: 6px;
    line-height: 1;
    cursor: pointer;

    svg {
      display: block;
    }
  }
`;

interface StyledEditorBlockProps {
  fullscreen: boolean;
}

const EditorBlock = styled.div<StyledEditorBlockProps>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  padding-bottom: 2px;
  color: ${({ theme }) => theme.colors.fontSubColor};

  .video-area {
    flex: 40vh;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    margin: ${({ fullscreen }) => (fullscreen ? '0 36px 0 22px' : '0 36px 0 0')};
    border-color: #4f4f4f;
    border-style: solid;
    border-width: 0;
    border-left-width: ${({ fullscreen }) => (fullscreen ? '1px' : '0')};

    .screen-box {
      flex: 1 1 auto;
      position: relative;
      border-color: #4f4f4f;
      border-style: solid;
      border-top-width: 1px;
      border-right-width: 0;
      border-bottom-width: 1px;
      border-left-width: 0;
      background-color: #000;
      box-sizing: border-box;
    }

    .chapter-box {
      flex: 0 0 256px;
      box-sizing: border-box;
    }
  }

  .timeline-area {
    flex: 0 0 180px;
    padding: ${({ fullscreen }) => (fullscreen ? '0 36px 0 22px' : '0 36px')};
  }

  .video-js {
    width: auto;
    height: 100%;

    .vjs-big-play-button {
      display: none;
    }
  }
`;

interface StyledFooterBlockProps {
  fullscreen: boolean;
}

const FooterBlock = styled.div<StyledFooterBlockProps>`
  display: block;
  min-height: 128px;
  padding: ${({ fullscreen }) => (fullscreen ? '16px 36px 62px' : '16px 36px 72px')};
  transition: all 300ms ease;

  .action-area {
    .ant-btn {
      float: right;
      width: 160px;
    }
  }
`;

export default ChapterEditor;
